import { Button } from '@amzn/awsui-components-react-v3';
import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import useClassroomManagement from '../../Common/useClassroomManagement';
import { useClassroomEligibility } from './useClassroomEligibility';
import { useButtonState } from './useButtonState';

interface CreateClassroomButtonProps {
    activity: ActivityData;
}

/**
 * CreateClassroomButton enables SMT users to create AWS Classrooms directly
 * from the Activity Details page, eliminating manual classroom creation in Builder Labs UI.
 *
 * The button is enabled when:
 * - Activity is Active
 * - Program is supported (Commercial, Partner, SkillsCenters)
 * - Has assigned instructors
 * - Has valid class size (>= 1)
 * - Meets LMS requirements for virtual delivery
 * - No existing classroom
 */
export const CreateClassroomButton = ({
    activity,
}: CreateClassroomButtonProps) => {
    const { handleSubmitEvent, submitting } = useClassroomManagement();

    const { isEligible, checks } = useClassroomEligibility(activity);

    const buttonState = useButtonState(activity, submitting);

    const iconName =
        activity.classrooms_status === ClassroomStatus.CLASSROOM_READY
            ? 'status-positive'
            : undefined;

    const isDisabled = !isEligible || buttonState.hasExistingClassroom;

    const getStatusMessages = () => {
        if (!isDisabled) return undefined;

        const requirements = [
            { met: checks.isActive, text: 'Set activity status to Active' },
            {
                met: checks.supportedProgram,
                text: 'Use a supported program type',
            },
            {
                met: checks.hasInstructors,
                text: 'Assign at least one instructor',
            },
            { met: checks.hasValidSize, text: 'Set class size to at least 1' },
            {
                met: checks.lmsRequirementsMet,
                text: 'Configure LMS requirements for virtual delivery',
            },
        ];

        const unmetRequirements = requirements
            .filter((req) => !req.met)
            .map((req) => req.text)
            .join(', ');

        return `To create a classroom: ${unmetRequirements}.`;
    };

    return (
        <Button
            data-testid="create-classroom-button"
            disabled={isDisabled}
            disabledReason={
                buttonState.hasExistingClassroom ? null : getStatusMessages()
            }
            iconName={iconName}
            loading={buttonState.isCreating}
            onClick={handleSubmitEvent}
        >
            {buttonState.text}
        </Button>
    );
};
